import { useEffect, useRef, useState, type FormEvent } from 'react';
import InkStroke from '../../../components/base/InkStroke';

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your name';
    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Please enter a message';
    if (formData.message.length > 500) newErrors.message = 'Message must be under 500 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const formEl = e.currentTarget as HTMLFormElement;
    const formDataObj = new FormData(formEl);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: formData.message,
      };

      const response = await fetch('https://natto.studio/contact', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch {
      setErrors({ submit: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-20 md:py-28 px-6 md:px-10 lg:px-16"
    >
      <div className="max-w-4xl mx-auto">
        <div
          className={`text-center mb-14 md:mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
            Get in Touch
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom">
            Let&rsquo;s talk
          </h2>
          <InkStroke className="mt-6" />
          <p className="font-body text-sm text-muted-umber max-w-lg mx-auto mt-6 leading-relaxed">
            Most enquiries lead to a brief conversation. Some lead to a long partnership.
            Either way, we reply promptly.
          </p>
        </div>

        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          {submitted ? (
            <div className="max-w-xl mx-auto text-center bg-white/60 rounded-lg p-10 md:p-14 border border-forest/20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-forest/10 mx-auto mb-5">
                <i className="ri-check-line text-forest text-xl" />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-deep-ink mb-3">
                Message received
              </h3>
              <p className="font-body text-sm text-muted-umber leading-relaxed">
                Thank you for reaching out. We will be in touch as soon as possible.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="max-w-xl mx-auto bg-white/60 rounded-lg p-6 md:p-10 border border-amber/10"
            >
              <div className="flex flex-col gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-2 block"
                  >
                    Name <span className="text-amber">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full font-body text-sm text-deep-ink bg-parchment/50 border border-amber/15 rounded-md px-4 py-3 focus:outline-none focus:border-amber/50 transition-colors"
                    placeholder="Your full name"
                  />
                  {errors.name && (
                    <p className="font-body text-xs text-amber mt-1.5">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-2 block"
                  >
                    Email <span className="text-amber">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full font-body text-sm text-deep-ink bg-parchment/50 border border-amber/15 rounded-md px-4 py-3 focus:outline-none focus:border-amber/50 transition-colors"
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="font-body text-xs text-amber mt-1.5">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-2 block"
                  >
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                    className="w-full font-body text-sm text-deep-ink bg-parchment/50 border border-amber/15 rounded-md px-4 py-3 focus:outline-none focus:border-amber/50 transition-colors"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-2 block"
                  >
                    Message <span className="text-amber">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="w-full font-body text-sm text-deep-ink bg-parchment/50 border border-amber/15 rounded-md px-4 py-3 focus:outline-none focus:border-amber/50 transition-colors resize-none"
                    placeholder="Tell us about your situation, your challenges, or what you’re exploring."
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    {errors.message && (
                      <p className="font-body text-xs text-amber">{errors.message}</p>
                    )}
                    <p
                      className={`font-body text-xs ml-auto ${
                        formData.message.length > 450
                          ? 'text-amber'
                          : 'text-muted-umber/60'
                      }`}
                    >
                      {formData.message.length}/500
                    </p>
                  </div>
                </div>

                {errors.submit && (
                  <p className="font-body text-xs text-amber text-center">
                    {errors.submit}
                  </p>
                )}

                <button
                  type="submit"
                  className="font-body text-sm font-semibold text-parchment bg-deep-ink px-8 py-3 rounded-md hover:bg-amber transition-colors duration-300 whitespace-nowrap mt-2"
                >
                  Send message
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}